// Composer: text, colour, bold, files, show-after

import React, { useRef, useState } from 'react';
import { FolderOpenOutlined, PaperClipOutlined } from '@ant-design/icons';
import {
    formatOperatorChatFileBytes,
    OPERATOR_SHOW_AFTER_PRESETS,
} from '../../services/operatorChatService';
import { OPERATOR_MESSAGE_COLOR_PRESETS } from '../../utils/operatorMessageSanitize';
import {
    OPERATOR_CHAT_MAX_FILE_BYTES,
    validateOperatorChatFile,
} from '../../services/operatorChatFileService';
import { formatOperatorDriveBytes } from '../../services/operatorDriveFileService';
import OperatorDrivePicker from '../operatorDrive/OperatorDrivePicker';

export default function OperatorChatComposer({
    uid,
    disabled,
    sending,
    uploadProgress,
    error,
    onSend,
    onClearError,
}) {
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const [activeColor, setActiveColor] = useState(OPERATOR_MESSAGE_COLOR_PRESETS[0].value);
    const [showAfterPresetId, setShowAfterPresetId] = useState('now');
    const [pendingFiles, setPendingFiles] = useState([]);
    const [pendingDrive, setPendingDrive] = useState([]);
    const [drivePickerOpen, setDrivePickerOpen] = useState(false);
    const [localError, setLocalError] = useState('');

    const runEditorCommand = (command, value = null) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        try {
            document.execCommand(command, false, value);
        } catch {
            /* ignore */
        }
    };

    const applyColor = (color) => {
        setActiveColor(color);
        runEditorCommand('foreColor', color);
    };

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        const next = [...pendingFiles];
        let err = '';
        for (const file of incoming) {
            const fileErr = validateOperatorChatFile(file);
            if (fileErr) {
                err = fileErr;
                continue;
            }
            next.push(file);
        }
        setPendingFiles(next);
        setLocalError(err);
        if (err) onClearError?.();
    };

    const removeFile = (index) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        setLocalError('');
        onClearError?.();
        const html = editorRef.current?.innerHTML || '';
        try {
            await onSend({
                bodyHtml: html,
                files: pendingFiles,
                driveItems: pendingDrive,
                showAfterPresetId,
            });
            if (editorRef.current) editorRef.current.innerHTML = '';
            setPendingFiles([]);
            setPendingDrive([]);
            setShowAfterPresetId('now');
        } catch (err) {
            setLocalError(err?.message || 'Failed to send.');
        }
    };

    const displayError = localError || error || '';

    return (
        <div className="op-msg-composer">
            <span className="op-msg-field-label">Show after (attention popup)</span>
            <div className="op-msg-delay-row" role="group" aria-label="Show after delay">
                {OPERATOR_SHOW_AFTER_PRESETS.map((preset) => (
                    <button
                        key={preset.id}
                        type="button"
                        className={`op-msg-delay-chip${showAfterPresetId === preset.id ? ' is-active' : ''}`}
                        onClick={() => setShowAfterPresetId(preset.id)}
                        disabled={disabled || sending}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            <div className="op-msg-toolbar" role="toolbar" aria-label="Message formatting">
                <div className="op-msg-tool-group" role="group" aria-label="Text style">
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('bold')}
                        title="Bold"
                        aria-label="Bold"
                        disabled={disabled || sending}
                    >
                        <strong>B</strong>
                    </button>
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('italic')}
                        title="Italic"
                        aria-label="Italic"
                        disabled={disabled || sending}
                    >
                        <em>I</em>
                    </button>
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('underline')}
                        title="Underline"
                        aria-label="Underline"
                        disabled={disabled || sending}
                    >
                        <span className="op-msg-tool-btn__u">U</span>
                    </button>
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('strikeThrough')}
                        title="Strikethrough"
                        aria-label="Strikethrough"
                        disabled={disabled || sending}
                    >
                        <span className="op-msg-tool-btn__s">S</span>
                    </button>
                </div>
                <div className="op-msg-tool-group" role="group" aria-label="Lists">
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('insertUnorderedList')}
                        title="Bullet list"
                        aria-label="Bullet list"
                        disabled={disabled || sending}
                    >
                        • List
                    </button>
                    <button
                        type="button"
                        className="op-msg-tool-btn"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runEditorCommand('insertOrderedList')}
                        title="Numbered list"
                        aria-label="Numbered list"
                        disabled={disabled || sending}
                    >
                        1. List
                    </button>
                </div>
                <div className="op-msg-tool-group op-msg-color-swatches" role="group" aria-label="Text colour">
                    {OPERATOR_MESSAGE_COLOR_PRESETS.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={`op-msg-color-swatch${activeColor === c.value ? ' is-active' : ''}`}
                            style={{ background: c.value }}
                            title={c.label}
                            aria-label={c.label}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applyColor(c.value)}
                            disabled={disabled || sending}
                        />
                    ))}
                    <label
                        className="op-msg-color-picker"
                        title="Custom colour"
                        aria-label="Custom text colour"
                    >
                        <span className="op-msg-color-picker__face" style={{ background: activeColor }} />
                        <input
                            type="color"
                            className="op-msg-color-input"
                            value={activeColor}
                            onMouseDown={(e) => e.preventDefault()}
                            onChange={(e) => applyColor(e.target.value)}
                            disabled={disabled || sending}
                        />
                    </label>
                </div>
                <button
                    type="button"
                    className="op-msg-tool-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach file"
                    aria-label="Attach file"
                    disabled={disabled || sending}
                >
                    <PaperClipOutlined /> Attach
                </button>
                <button
                    type="button"
                    className="op-msg-tool-btn"
                    onClick={() => setDrivePickerOpen(true)}
                    title="Insert from Smoke Drive"
                    aria-label="Insert from Smoke Drive"
                    disabled={disabled || sending || !uid}
                >
                    <FolderOpenOutlined /> Smoke Drive
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = '';
                    }}
                />
            </div>

            <div
                ref={editorRef}
                className="op-msg-editor"
                contentEditable={!disabled && !sending}
                role="textbox"
                aria-multiline="true"
                data-placeholder="Write a message…"
                suppressContentEditableWarning
            />

            {pendingFiles.length > 0 ? (
                <ul className="op-msg-pending-files">
                    {pendingFiles.map((file, index) => (
                        <li key={`${file.name}-${index}`}>
                            <span>
                                {file.name} · {formatOperatorChatFileBytes(file.size)}
                            </span>
                            <button
                                type="button"
                                className="op-msg-link-btn"
                                onClick={() => removeFile(index)}
                                disabled={sending}
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {pendingDrive.length > 0 ? (
                <ul className="op-msg-pending-files">
                    {pendingDrive.map((item) => (
                        <li key={item.id}>
                            <span>
                                Smoke Drive · {item.name} · {formatOperatorDriveBytes(item.sizeBytes)}
                            </span>
                            <button
                                type="button"
                                className="op-msg-link-btn"
                                onClick={() =>
                                    setPendingDrive((prev) => prev.filter((row) => row.id !== item.id))
                                }
                                disabled={sending}
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {pendingFiles.length === 0 && pendingDrive.length === 0 ? (
                <p className="op-msg-hint">
                    Files up to {formatOperatorChatFileBytes(OPERATOR_CHAT_MAX_FILE_BYTES)}, or insert
                    a Smoke Drive card without a second upload.
                </p>
            ) : null}

            <OperatorDrivePicker
                uid={uid}
                open={drivePickerOpen}
                onClose={() => setDrivePickerOpen(false)}
                onPick={(items) => {
                    setPendingDrive((prev) => {
                        const byId = new Map(prev.map((row) => [row.id, row]));
                        items.forEach((item) => byId.set(item.id, item));
                        return Array.from(byId.values());
                    });
                    setLocalError('');
                }}
            />

            {uploadProgress ? (
                <p className="op-msg-hint" role="status">
                    Uploading {uploadProgress.fileName}… {uploadProgress.pct}%
                </p>
            ) : null}

            {displayError ? <p className="op-msg-error">{displayError}</p> : null}

            <div className="op-msg-actions">
                <button
                    type="button"
                    className="op-msg-btn op-msg-btn--primary"
                    disabled={disabled || sending}
                    onClick={handleSend}
                >
                    {sending ? 'Sending…' : 'Send'}
                </button>
            </div>
        </div>
    );
}
